// src/components/AdminDashboard/sections/DiscountCodesSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  CircularProgress,
  Tooltip,
  Box,
  Button,
  Paper
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
// Removed styled-components import - now using Tailwind classes

function DiscountCodesSection({
  discountCodes,
  loadingDiscountCodes,
  deleteError,
  handleDelete
}) {
  return (
    <Paper className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 mb-8 shadow-admin-card border border-accent-teal/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-admin-card-hover hover:border-accent-teal/20 relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-0.5 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100">
      <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-accent-teal/10">
        <Box display="flex" alignItems="center" gap={1}>
          <LocalOfferIcon className="text-accent-teal" />
          <Typography variant="h6" className="text-accent-grey font-bold text-xl m-0">
            Discount Codes
          </Typography>
        </Box>
        <Button
          component={Link}
          to="/admin/discount-code/create"
          variant="contained"
          startIcon={<AddIcon />}
          className="bg-gradient-to-r from-accent-teal to-accent-grey text-white rounded-lg px-6 py-3 font-semibold normal-case shadow-admin-button transition-all duration-300 hover:from-accent-grey hover:to-accent-teal hover:-translate-y-0.5 hover:shadow-admin-button-hover active:translate-y-0 w-full md:w-auto md:px-6 md:py-3"
        >
          Create New Discount Code
        </Button>
      </div>
      
      <div className="overflow-x-auto w-full mb-6 rounded-xl shadow-lg bg-white">
        <Table className="min-w-[700px] [&_.MuiTableHead-root]:bg-gradient-to-r [&_.MuiTableHead-root]:from-accent-teal [&_.MuiTableHead-root]:to-accent-grey [&_.MuiTableCell-head]:text-white [&_.MuiTableCell-head]:font-bold [&_.MuiTableCell-head]:text-sm [&_.MuiTableCell-head]:uppercase [&_.MuiTableCell-head]:tracking-wider [&_.MuiTableCell-head]:border-none [&_.MuiTableCell-head]:p-4 [&_.MuiTableBody-root_.MuiTableRow-root]:transition-colors [&_.MuiTableBody-root_.MuiTableRow-root]:duration-200 [&_.MuiTableBody-root_.MuiTableRow-root:hover]:bg-accent-teal/5 [&_.MuiTableBody-root_.MuiTableRow-root:nth-of-type(even)]:bg-accent-teal/[0.02] [&_.MuiTableCell-body]:border-b [&_.MuiTableCell-body]:border-accent-grey/10 [&_.MuiTableCell-body]:text-accent-grey [&_.MuiTableCell-body]:font-medium [&_.MuiTableCell-body]:p-4 [&_.MuiTableCell-body_a]:text-accent-teal [&_.MuiTableCell-body_a]:no-underline [&_.MuiTableCell-body_a]:font-semibold [&_.MuiTableCell-body_a]:transition-colors [&_.MuiTableCell-body_a]:duration-200 [&_.MuiTableCell-body_a:hover]:text-accent-orange [&_.MuiTableCell-body_a:hover]:underline">
          <TableHead>
            <TableRow>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Code</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Description</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Type</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Value</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Expiration Date</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Usage Limit</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Times Used</TableCell>
              <TableCell className="text-accent-grey font-bold text-sm uppercase tracking-wider">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingDiscountCodes ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <div className="flex justify-center items-center p-10">
                    <CircularProgress className="text-accent-teal" />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              discountCodes.map((code) => (
                <TableRow key={code.id} className="hover:bg-accent-teal/5">
                  <TableCell>{code.code}</TableCell>
                  <TableCell>{code.description || 'N/A'}</TableCell>
                  <TableCell>{code.discountType || 'N/A'}</TableCell>
                  <TableCell>{code.value || 'N/A'}</TableCell>
                  <TableCell>
                    {code.expirationDate
                      ? new Date(code.expirationDate).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>{code.usageLimit || 'N/A'}</TableCell>
                  <TableCell>{code.timesUsed || '0'}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit Discount Code">
                        <Button
                          component={Link}
                          to={`/admin/discount-code/update/${code.id}`}
                          variant="contained"
                          startIcon={<EditIcon />}
                          className="bg-gradient-to-r from-accent-teal to-accent-grey text-white rounded-lg px-4 py-2 font-semibold normal-case shadow-md transition-all duration-300 hover:from-accent-grey hover:to-accent-teal hover:-translate-y-0.5 hover:shadow-lg"
                        >
                          Edit
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete Discount Code">
                        <Button
                          variant="contained"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDelete(code.id)}
                          className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg px-4 py-2 font-semibold normal-case shadow-md transition-all duration-300 hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-lg"
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {!loadingDiscountCodes && discountCodes.length === 0 && !deleteError && (
          <div className="text-center text-accent-grey mt-8 p-10 bg-gradient-to-br from-accent-teal/5 to-accent-orange/5 rounded-xl border-2 border-dashed border-accent-teal/30 text-lg font-medium">
            No discount codes available.
          </div>
        )}
        {deleteError && (
          <div className="text-center text-red-500 mt-5 p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-lg border-l-4 border-red-500 font-semibold">
            {deleteError}
          </div>
        )}
      </div>
    </Paper>
  );
}

DiscountCodesSection.propTypes = {
  discountCodes: PropTypes.array.isRequired,
  loadingDiscountCodes: PropTypes.bool,
  deleteError: PropTypes.string,
  handleDelete: PropTypes.func.isRequired
};

export default DiscountCodesSection;
